import { IMessageDrawerOptions } from '../globalInterfaces/globalInterfaces';
import { IMessage, MessageType } from './types';

const REG_EXP = {
	URL: /(((https?:\/\/)|(www\.)|(https?:\/\/)(www\.))[-a-zA-Z0-9@:%._+()~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*))/gi,
	USER_MENTION: /\[user\sid=(?<userId>.*?)](?<username>.*?)(\[\/user])/,
	JUMP_LINK: /JUMP_LINK__&&__(\w+)__&&__JUMP_LINK/i,
};

/* Add another keyword if this is will be need
for example:
    JAVA: {
        PATTERN: /\bjava\b/i,
        URL: '/learn/courses/java-introduction',
    },
*/
const JUMP_LINKS = {
	PYTHON: {
		PATTERN: /\bpython\b/i,
		URL: '/learn/courses/python-introduction',
	},
};

const linkReplacement = (message: string): { text: string; link: string } => {
	if (message.indexOf('https://') === 0 || message.indexOf('http://') === 0) {
		return { text: message, link: message };
	}
	return { text: message, link: `https://${message}` };
};

const userMentionReplacement = (
	message: string,
): { text: string; userId: string } => {
	const match = REG_EXP.USER_MENTION.exec(message);
	if (match) {
		return {
			text: match.groups.username,
			userId: match.groups.userId.replace(/"/g, ''),
		};
	}
	return { text: message, userId: null };
};

const jumpLinkReplacement = (
	message: string,
): { text?: string; link?: string } => {
	const match = REG_EXP.JUMP_LINK.exec(message);

	return {
		text: match[1],
		link: JUMP_LINKS[match[1].toUpperCase()].URL,
	};
};

const sanitizeMessage = (
	message: string,
	options: IMessageDrawerOptions,
): { type?: MessageType; text?: string; link?: string } => {
	if (REG_EXP.URL.test(message) && options.enableLinkMessage) {
		return {
			type: MessageType.link,
			...linkReplacement(message),
		};
	}
	if (REG_EXP.USER_MENTION.test(message) && options.enableUserMention) {
		return {
			type: MessageType.userMention,
			...userMentionReplacement(message),
		};
	}

	if (REG_EXP.JUMP_LINK.test(message) && options.enableJumpLink) {
		return {
			type: MessageType.jumpLink,
			...jumpLinkReplacement(message),
		};
	}

	return {
		type: MessageType.text,
		text: message,
	};
};

const createMessageRegExpPattern = (): string => Object.keys(REG_EXP).reduce((acc, current, i) => `${acc}${REG_EXP[current].source}${
	i === Object.keys(REG_EXP).length - 1 ? '' : '|'
}`, '');

const modifyMessageWithJumpLinks = (
	message: string,
	enableJumpLink: boolean,
): string => {
	if (!enableJumpLink) {
		return message;
	}
	let newMesage = message;
	Object.keys(JUMP_LINKS).forEach((key) => {
		const match = JUMP_LINKS[key].PATTERN.exec(message);

		if (match && match[0]) {
			newMesage = newMesage.replace(
				JUMP_LINKS[key].PATTERN,
				`JUMP_LINK__&&__${match[0]}__&&__JUMP_LINK`,
			);
		}
	});
	return newMesage;
};

export const messageSplitter = (
	message: string,
	options: IMessageDrawerOptions,
): IMessage[] => {
	const modifyMessage: string = modifyMessageWithJumpLinks(
		message,
		options.enableJumpLink,
	);

	const globalRegex = new RegExp(createMessageRegExpPattern(), 'g');

	const indexes = [];
	let match;
	while ((match = globalRegex.exec(modifyMessage)) !== null) {
		indexes.push(match.index, globalRegex.lastIndex);
	}

	const splitOn = (data, ...indices): IMessage[] => [0, ...indices].reduce((acc, n, i, m) => {
		const slicedText = data.slice(n, m[i + 1]);
		if (!slicedText.length) {
			return acc;
		}

		const el = {
			...sanitizeMessage(slicedText, options),
			id: Date.now() + Math.random(),
		};
		return [...acc, el];
	}, []);

	return splitOn(modifyMessage, ...indexes);
};
